import React from 'react'
import PropTypes from 'prop-types'

import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table'
import BaseTableConfig from 'shared/classes/BaseTableConfig'
import InnerLoader from 'shared/components/Loaders/InnerLoader'
import BaseTableHeader from 'shared/components/BaseTable/BaseTableHeader'
import { Button } from 'react-bootstrap'
import TableAlert from 'shared/components/BaseTable/TableAlert'

import classnames from 'classnames'
import {
  fireCsvDownloadEvent,
  fireTableColumnFilterEvent,
  fireTableSortEvent,
  fireTableFilterButtonEvent,
} from 'Store/Analytics/actions'
import { ANALYTICS_FEATURES } from 'shared/utilities/ga4'
import './style.scss'

// Adapter: convert legacy column definitions to TanStack columns
function adaptColumns(legacyColumns, { expandRow, columnFilters, setColumnFilters }) {
  return legacyColumns
    .filter(col => col && !col.hidden)
    .map(col => {
      const tanstackCol = {
        id: col.dataField,
        accessorKey: col.dataField,
        header: col.text || '',
        enableSorting: col.sort !== false,
        meta: {
          legacyCol: col,
          classes: col.classes,
          headerClasses: col.headerClasses,
          events: col.events,
          component: col.component,
        },
      }

      // Cell renderer
      if (col.formatter) {
        tanstackCol.cell = ({ getValue, row }) => {
          const value = getValue()
          return col.formatter(value, row.original)
        }
      }

      // Filter
      if (col.filter) {
        tanstackCol.enableColumnFilter = true
        tanstackCol.meta.filterConfig = col.filter
        // Assign the correct filter function
        if (col.filter.props && (col.filter.props.type === 'multiselect' || (col.filter.props.options && typeof col.filter.props.options === 'object'))) {
          tanstackCol.filterFn = 'multiSelect'
        } else {
          tanstackCol.filterFn = 'text'
        }
      } else {
        tanstackCol.enableColumnFilter = false
      }

      return tanstackCol
    })
}

// Text filter matching function
function textFilterFn(row, columnId, filterValue) {
  if (!filterValue) return true
  const cellValue = row.getValue(columnId)
  if (cellValue == null) return false
  return String(cellValue).toLowerCase().includes(String(filterValue).toLowerCase())
}

// Multi-select filter matching function
function multiSelectFilterFn(row, columnId, filterValue) {
  if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) return true
  const cellValue = row.getValue(columnId)
  if (cellValue == null) return false
  const cellStr = String(cellValue).toLowerCase()
  const values = Array.isArray(filterValue) ? filterValue : [filterValue]
  return values.some(v => cellStr.includes(String(v).toLowerCase()))
}

// CSV export helper
function exportToCsv(table, columns, filename) {
  const visibleColumns = columns.filter(col => col && !col.hidden && col.csvExport !== false)
  const headers = visibleColumns.map(col => col.text || col.dataField)
  const rows = table.getFilteredRowModel().rows.map(row => {
    return visibleColumns.map(col => {
      const value = row.original[col.dataField]
      if (col.csvFormatter) {
        return col.csvFormatter(value, row.original)
      }
      if (value == null) return ''
      return String(value).replace(/"/g, '""')
    })
  })

  const csvContent = [
    headers.map(h => `"${h}"`).join(','),
    ...rows.map(r => r.map(v => `"${v}"`).join(',')),
  ].join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}

// Inner table component (functional, uses hooks)
function BaseTableInner({
  records,
  tableConfig,
  expandable,
  loading,
  nested,
  includeHeader,
  headerComponent,
  headerClass,
  recordsSize,
  request,
  error,
  errorAction,
  wrapperClasses,
  classes,
  globalTableState,
  setTableState,
  dispatch,
  page: pageProp,
  annotationStart,
  advancedSearchDatasets,
  csvBaseFileName,
  baseTableConfig,
  analyticsFeature,
}) {
  const [expanded, setExpanded] = React.useState([])
  const [expandedRowProps, setExpandedRowProps] = React.useState(null)
  const [expandedRowComponent, setExpandedRowComponent] = React.useState(null)
  const [columnFilters, setColumnFilters] = React.useState([])
  const [sorting, setSorting] = React.useState(
    tableConfig.defaultSorted
      ? tableConfig.defaultSorted.map(s => ({
          id: s.dataField,
          desc: s.order === 'desc',
        }))
      : []
  )
  const [pagination, setPagination] = React.useState({
    pageIndex: (globalTableState.page || 1) - 1,
    pageSize: globalTableState.sizePerPage || 10,
  })
  const prevColumnFiltersRef = React.useRef([])
  const prevSortingRef = React.useRef([])
  const sortingAnalyticsReadyRef = React.useRef(false)

  React.useEffect(() => {
    if (!dispatch || !analyticsFeature) return

    baseTableConfig.onFilterSelect = (filterName, filterValue) => {
      dispatch(fireTableFilterButtonEvent(filterName, filterValue, analyticsFeature))
    }

    return () => {
      baseTableConfig.onFilterSelect = null
    }
  }, [dispatch, analyticsFeature, baseTableConfig])

  React.useEffect(() => {
    if (!dispatch || !analyticsFeature) return

    columnFilters.forEach(filter => {
      const prev = prevColumnFiltersRef.current.find(item => item.id === filter.id)
      if (!prev || prev.value !== filter.value) {
        dispatch(fireTableColumnFilterEvent(filter.id, filter.value, analyticsFeature))
      }
    })
    prevColumnFiltersRef.current = columnFilters
  }, [columnFilters, dispatch, analyticsFeature])

  React.useEffect(() => {
    if (!dispatch || !analyticsFeature) return

    if (!sortingAnalyticsReadyRef.current) {
      sortingAnalyticsReadyRef.current = true
      prevSortingRef.current = sorting
      return
    }

    sorting.forEach(sort => {
      const prev = prevSortingRef.current.find(item => item.id === sort.id)
      if (!prev || prev.desc !== sort.desc) {
        dispatch(fireTableSortEvent(sort.id, sort.desc ? 'desc' : 'asc', analyticsFeature))
      }
    })
    prevSortingRef.current = sorting
  }, [sorting, dispatch, analyticsFeature])

  const setExpandedContent = React.useCallback(
    ({ component, expandedRowProps: rowProps, rowId, e }) => {
      if (
        expanded.includes(rowId) &&
        rowProps.content === (expandedRowProps || {}).content
      ) {
        // Toggle off
        if (e.type === 'mouseenter') return
        setExpanded([])
        setExpandedRowProps(null)
        setExpandedRowComponent(null)
        e.currentTarget
          .closest('tbody')
          .querySelectorAll('td')
          .forEach(col => col.classList.remove('table-column--active'))
      } else {
        setExpanded([rowId])
        setExpandedRowProps(rowProps)
        // Intentionally NOT storing the component — we don't want the
        // ExpandedLinkRow "(below)" pattern to render an extra TR. The
        // clicked cell wraps inline via the table-column--active CSS rule
        // instead.
        setExpandedRowComponent(null)
        e.currentTarget
          .closest('tbody')
          .querySelectorAll('td')
          .forEach(col => col.classList.remove('table-column--active'))
        e.currentTarget.classList.add('table-column--active')
      }
    },
    [expanded, expandedRowProps]
  )

  const constructFilter = React.useCallback(
    filterType => {
      return filterType({
        placeholder: 'Search...',
        getFilter: filter => {
          const filterKey = (Math.random() * 100000).toString()
          baseTableConfig.filters = { ...baseTableConfig.filters, [filterKey]: filter }
        },
      })
    },
    [baseTableConfig]
  )

  const legacyColumns = React.useMemo(
    () =>
      tableConfig.getColumns({
        page: pageProp,
        // getTableColumns expects `columnExpandFunction` — the old name
        // `expandColumnFunction` from the tanstack migration silently broke
        // every click-to-expand cell (Status Description, etc).
        columnExpandFunction: setExpandedContent,
        constructFilter,
        baseTableConfig,
        rowExample: records[0],
        dispatch,
        annotationStart,
        advancedSearchDatasets,
      }),
    [records, tableConfig, pageProp, dispatch, annotationStart, advancedSearchDatasets, setExpandedContent, constructFilter, baseTableConfig]
  )

  const columns = React.useMemo(
    () => adaptColumns(legacyColumns, { columnFilters, setColumnFilters }),
    [legacyColumns, columnFilters]
  )

  // Build filter functions map from baseTableConfig
  const columnFilterFns = React.useMemo(() => {
    const fns = {}
    legacyColumns.forEach(col => {
      if (!col) return
      if (col.filter && col.filter.props) {
        const filterProps = col.filter.props || {}
        if (filterProps.type === 'multiselect' || (filterProps.options && typeof filterProps.options === 'object')) {
          fns[col.dataField] = multiSelectFilterFn
        } else {
          fns[col.dataField] = textFilterFn
        }
      } else if (col.filter) {
        fns[col.dataField] = textFilterFn
      }
    })
    return fns
  }, [legacyColumns])

  // Wire baseTableConfig to TanStack's setColumnFilters
  React.useEffect(() => {
    baseTableConfig._setColumnFilters = setColumnFilters
    return () => { baseTableConfig._setColumnFilters = null }
  }, [baseTableConfig, setColumnFilters])

  // Also handle text filters from constructFilter
  const globalFilterFn = React.useCallback(
    (row, columnId, filterValue) => {
      const fn = columnFilterFns[columnId]
      if (fn) return fn(row, columnId, filterValue)
      return textFilterFn(row, columnId, filterValue)
    },
    [columnFilterFns]
  )

  const table = useReactTable({
    data: records,
    columns,
    state: {
      sorting,
      columnFilters,
      pagination,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    filterFns: {
      text: textFilterFn,
      multiSelect: multiSelectFilterFn,
    },
    getRowId: row => String(row[tableConfig.keyField]),
  })

  const handlePageChange = React.useCallback(
    (newPage) => {
      const newPagination = { ...pagination, pageIndex: newPage - 1 }
      setPagination(newPagination)
      if (setTableState) {
        setTableState({
          ...globalTableState,
          page: newPage,
          sizePerPage: pagination.pageSize,
        })
      }
    },
    [pagination, setTableState, globalTableState]
  )

  const handleSizePerPageChange = React.useCallback(
    (newSize) => {
      const newPagination = { pageIndex: 0, pageSize: newSize }
      setPagination(newPagination)
      if (setTableState) {
        setTableState({
          ...globalTableState,
          page: 1,
          sizePerPage: newSize,
        })
      }
    },
    [setTableState, globalTableState]
  )

  const constructCsvFilename = React.useCallback(
    (selectedFilters) => {
      const filters = Object.keys(selectedFilters)
        .map(key => {
          if (selectedFilters[key]) {
            return key.split('__')[1].trim()
          }
        })
        .filter(f => f)
        .join('_')

      return `${csvBaseFileName}${filters ? '__' + filters : ''}.csv`.replace(' ', '_').toLowerCase()
    },
    [csvBaseFileName]
  )

  const handleCsvClick = React.useCallback(() => {
    if (dispatch) {
      dispatch(fireCsvDownloadEvent(
        constructCsvFilename(baseTableConfig.selectedFilters),
        analyticsFeature || ANALYTICS_FEATURES.TABLE
      ))
    }
    exportToCsv(table, legacyColumns, constructCsvFilename(baseTableConfig.selectedFilters))
  }, [dispatch, constructCsvFilename, baseTableConfig, table, legacyColumns, analyticsFeature])

  const filteredRowCount = table.getFilteredRowModel().rows.length
  const currentPage = pagination.pageIndex + 1
  const totalPages = table.getPageCount()

  // Pagination props compatible with BaseTableHeader
  const paginationProps = {
    totalSize: records.length,
    dataSize: filteredRowCount,
    page: currentPage,
    sizePerPage: pagination.pageSize,
    onPageChange: handlePageChange,
    onSizePerPageChange: handleSizePerPageChange,
  }

  // CSV props compatible with CsvButton (export handled by handleCsvClick)
  const csvProps = {}

  const renderExpandedRow = (row) => {
    const rowId = row.id
    if (!expanded.includes(rowId) && !expanded.includes(row.original[tableConfig.keyField])) return null

    const isNestedTable = component => {
      if (!component) return false
      return (
        component.name === 'NestedTable' ||
        (typeof component === 'function' && component.prototype && component.prototype.isReactComponent)
      )
    }

    if (isNestedTable(expandedRowComponent)) {
      const NestedTableComp = expandedRowComponent
      return (
        <tr className="expanding-row" key={`expanded-${rowId}`}>
          <td colSpan={columns.length} className="reset-expansion-style">
            <div className="table-row--nested-bumper">
              <NestedTableComp nested={true} {...expandedRowProps} />
            </div>
          </td>
        </tr>
      )
    } else if (expandedRowProps && expandedRowComponent) {
      const ExpandedComp = expandedRowComponent
      return (
        <tr className="expanding-row" key={`expanded-${rowId}`}>
          <td colSpan={columns.length} className="reset-expansion-style">
            <ExpandedComp {...expandedRowProps} />
          </td>
        </tr>
      )
    }
    return null
  }

  // Render sort indicator
  const renderSortIndicator = (column) => {
    if (!column.getCanSort()) return null
    const sorted = column.getIsSorted()
    return (
      <span className="order-4">
        {sorted === 'asc' ? ' ▲' : sorted === 'desc' ? ' ▼' : ' ⇅'}
      </span>
    )
  }

  // Render column filter input
  const renderColumnFilter = (column) => {
    const meta = column.columnDef.meta
    if (!meta || !meta.filterConfig) return null

    // Check if it's a hidden filter (from baseTableConfig filterPrototypes)
    const filterConfig = meta.filterConfig
    if (filterConfig && filterConfig.props && filterConfig.props.type === 'multiselect') {
      // Multi-select filters are controlled via button sets, hide the input
      return <div className="hide-filter" />
    }

    // Text filter
    return (
      <input
        type="text"
        className="text-filter form-control"
        placeholder="Search..."
        value={column.getFilterValue() || ''}
        onChange={e => column.setFilterValue(e.target.value || undefined)}
      />
    )
  }

  const HeaderComponent = headerComponent

  // Generate page numbers for pagination
  const pageNumbers = []
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i)
  }

  return (
    <div
      className={`base-table ${wrapperClasses || ''}`}
      data-test-id="base-table"
      data-resource={tableConfig.resourceConstant}
      key={`table-${tableConfig.keyField}`}
    >
      {error && (
        <TableAlert
          variant="danger"
          textType="text-danger"
          message={error.message}
          action={errorAction}
        />
      )}
      {!nested && !!includeHeader && <div>{HeaderComponent}</div>}
      {error && (
        <TableAlert
          variant="danger"
          textType="text-danger"
          message={error.message}
          action={errorAction}
        />
      )}
      <div className="base-table__header-body-wrapper">
        <div className="base-table__header-body-inner-wrapper">
          <BaseTableHeader
            headerClass={headerClass}
            records={records}
            recordsSize={recordsSize}
            csvProps={csvProps}
            paginationProps={paginationProps}
            handleCsvClick={handleCsvClick}
            resourceConstant={tableConfig.resourceConstant}
            request={request}
            filterButtonSets={
              baseTableConfig.filterButtonSets[tableConfig.resourceConstant]
            }
            selectedFilters={baseTableConfig.selectedFilters}
            sizePerPageOptions={[10, 50, 100]}
            onSizePerPageChange={handleSizePerPageChange}
          />
          <table
            className={classnames(
              'table table-bordered table-sm table-condensed',
              classes,
              { 'no-expand': !expandable }
            )}
          >
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      data-field={header.column.id}
                      className={classnames(
                        header.column.columnDef.meta?.headerClasses,
                        { sortable: header.column.getCanSort() }
                      )}
                      onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                      style={{ cursor: header.column.getCanSort() ? 'pointer' : 'default' }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {renderSortIndicator(header.column)}
                      {header.column.getCanFilter() && renderColumnFilter(header.column)}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length === 0 && !loading ? (
                <tr>
                  <td colSpan={columns.length} className="react-bs-table-no-data">
                    <TableAlert
                      textType="text-dark"
                      variant="light"
                      message={'No records found'}
                      buttonText="Clear Filters"
                      buttonVariant="outline-dark"
                    />
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map(row => {
                  const rowClasses = tableConfig.tableRowClasses
                    ? typeof tableConfig.tableRowClasses === 'function'
                      ? tableConfig.tableRowClasses(row.original)
                      : tableConfig.tableRowClasses
                    : ''
                  const isExpanded =
                    expanded.includes(row.id) ||
                    expanded.includes(row.original[tableConfig.keyField])

                  return (
                    <React.Fragment key={row.id}>
                      <tr className={classnames(rowClasses, { 'table-row--expanded': isExpanded })}>
                        {row.getVisibleCells().map(cell => {
                          const meta = cell.column.columnDef.meta || {}
                          const cellClasses = classnames(
                            meta.classes,
                            { 'expandable-cell': expandable && meta.events?.onClick }
                          )
                          return (
                            <td
                              key={cell.id}
                              data-field={cell.column.id}
                              className={cellClasses}
                              onClick={e => {
                                if (meta.events?.onClick) {
                                  meta.events.onClick(e, cell.column, cell.column.id, row.original)
                                }
                              }}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          )
                        })}
                      </tr>
                      {expandable && isExpanded && renderExpandedRow(row)}
                    </React.Fragment>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {loading && <InnerLoader />}

      {!nested &&
        !!includeHeader &&
        pagination.pageSize < records.length && (
          <div>
            <ul className="pagination">
              {pageNumbers.map(num => (
                <li key={num} className="page-item">
                  <Button
                    variant={currentPage === num ? 'dark' : 'light'}
                    onClick={e => {
                      e.preventDefault()
                      handlePageChange(num)
                    }}
                  >
                    {num}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
    </div>
  )
}

// Wrapper class component to maintain the same API
class BaseTable extends React.Component {
  constructor(props) {
    super(props)
    this.baseTableConfig = new BaseTableConfig({ component: this })
  }

  render() {
    return (
      <BaseTableInner
        {...this.props}
        baseTableConfig={this.baseTableConfig}
      />
    )
  }
}

BaseTable.defaultProps = {
  globalTableState: {},
  expandable: true,
  includeHeader: true,
  nested: false,
  recordsSize: 0,
  request: undefined,
}

BaseTable.propTypes = {
  annotationStart: PropTypes.string,
  classes: PropTypes.string,
  dispatch: PropTypes.func,
  error: PropTypes.object,
  expandable: PropTypes.bool,
  globalTableState: PropTypes.object,
  headerComponent: PropTypes.object,
  loading: PropTypes.bool,
  nested: PropTypes.bool,
  records: PropTypes.array,
  recordsSize: PropTypes.number,
  tableConfig: PropTypes.object,
  advancedSearchDatasets: PropTypes.array,
  page: PropTypes.string,
}

export default BaseTable
